import React from 'react';
import type { HeadingSize } from '@/components/ui';
import { Flex, Heading, Text } from '@/components/ui';

import clsx from 'clsx';

export type PageHeaderSize = 'small' | 'medium' | 'large';

const PageTitleSizes: Record<PageHeaderSize, HeadingSize> = {
  small: 'h6',
  medium: 'h4',
  large: 'h3',
};

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  tagline?: string;
  description?: string;
  startSlot?: React.ReactNode;
  endSlot?: React.ReactNode;
  className?: string;
  size?: PageHeaderSize;
  divider?: boolean;
  style?: React.CSSProperties;
}

/**
 * PageHeader component for displaying page titles, subtitles, tagline, and optional slots.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  tagline,
  description,
  startSlot,
  endSlot,
  className,
  size = 'large',
  divider = false,
  style,
  ...rest
}) => {
  return (
    <Flex
      className={clsx(
        'page-header',
        `page-header--size-${size}`,
        divider && 'page-header--with-divider',
        className
      )}
      style={style}
      {...rest}
    >
      {startSlot && <div className="page-header__start-slot">{startSlot}</div>}
      <Flex direction="column" className="page-header__content">
        {tagline && (
          <Text size="tagline" className="page-header__tagline">
            {tagline}
          </Text>
        )}
        {title && (
          <Heading
            size={PageTitleSizes[size]}
            className={`page-header__title page-header__title--size-${size}`}
          >
            {title}
          </Heading>
        )}
        {subtitle && (
          <Text size="medium" className="page-header__subtitle">
            {subtitle}
          </Text>
        )}
        {description && (
          <div className="page-header__description">{description}</div>
        )}
      </Flex>
      {endSlot && <div className="page-header__end-slot">{endSlot}</div>}
    </Flex>
  );
};

export { PageHeader };
